import {
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Tag,
  message,
} from "antd";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { BiSearch } from "react-icons/bi";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
// import { defaultStatus } from "../../../../../helper/options";
import { DeleteOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { serverUrl } from "../../../../nestserver";
import plusicon from "../../../../resources/plus.svg";
import { academicYearFromSettings } from "../../../../helper/academicYear";
import DatePicker from "../../../../components/inputs/MomentDatePicker";

// The three subject groups a template is built from. Kept in one place so the
// picker, the exam tables and the payload can never drift apart.
const SUBJECT_GROUPS = [
  { key: "mandatory", label: "Mandatory Subjects", flag: "is_mandatory" },
  { key: "language", label: "Languages", flag: "is_language" },
  { key: "curricular", label: "Co-Curricular", flag: "is_co_curricular" },
];

const TemplateCard = () => {
  const { Search } = Input;
  const { Option } = Select;
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // const [search, setSearch] = useState(null);
  const [addTemplate, setAddTemplate] = useState(false);
  const [classId, setClassId] = useState(null);
  const [getClassId, setGetClassId] = useState(
    sessionStorage.getItem("instructor_v2_class_id") || null,
  );
  // Step 1: which subjects are ticked, per group. Step 2: the exam chosen for
  // each ticked subject. Both keyed by SUBJECT_GROUPS[].key.
  const [selectedSubjects, setSelectedSubjects] = useState({
    mandatory: [],
    language: [],
    curricular: [],
  });
  const [examBySubject, setExamBySubject] = useState({
    mandatory: {},
    language: {},
    curricular: {},
  });
  const [searchQuery, setSearchQuery] = useState(
    sessionStorage.getItem("instructor_v2_search_query") || "",
  );

  function tagRender(props) {
    const { label, closable, onClose } = props;

    const onPreventMouseDown = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };
    return (
      <Tag
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={onClose}
        style={{ marginRight: 3 }}
        className="flex items-center"
      >
        {label}
      </Tag>
    );
  }
  // const dateFormat = "YYYY";

  // fetch the school's configured academic year (Settings > Class & Academic)
  const fetchDefaultDates = async () => {
    const res = await axios({
      method: "get",
      url: serverUrl + "/classes/default-dates",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  };
  const defaultDates = useQuery("classes/default-dates", fetchDefaultDates);
  const academicYear = academicYearFromSettings(defaultDates?.data);

  // fetch class
  const fetchClasses = async () => {
    const res = await axios({
      method: "get",
      url: serverUrl + "/teachers/classes",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  };
  const classes = useQuery("teachers/classes", fetchClasses);

  const handleGetClass = (id) => {
    setGetClassId(id);
    sessionStorage.setItem("instructor_v2_class_id", id);
  };
  // fetch subject
  async function fetchSubjects() {
    const res = await axios({
      method: "get",
      url: serverUrl + "/subjects/",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });

    return res.data;
  }
  const subjects = useQuery("subjects", fetchSubjects, {
    enabled: !!classId,
  });

  const removeSubjectsData = () => {
    queryClient.invalidateQueries("subjects", { data: [] });
  };
  // Subjects belonging to the chosen class, for one group.
  const groupSubjects = (flag) =>
    subjects?.isSuccess && classId
      ? subjects.data.filter(
          (s) => s?.class?._id === classId && s?.[flag] === true,
        )
      : [];

  // Exams available for one subject within its group.
  const subjectExams = (flag, subjectId) =>
    exam?.isSuccess
      ? exam.data.filter(
          (e) =>
            e?.class?._id === classId &&
            e?.subject?.[flag] === true &&
            e?.subject?._id === subjectId,
        )
      : [];

  // Step 1 tick/untick. Unticking also clears that subject's chosen exam so a
  // stale pick can't leak into the payload if it is ticked again later.
  const handleToggleSubjects = (groupKey, checkedIds) => {
    setSelectedSubjects((prev) => ({ ...prev, [groupKey]: checkedIds }));
    setExamBySubject((prev) => {
      const kept = {};
      checkedIds.forEach((id) => {
        if (prev[groupKey]?.[id]) kept[id] = prev[groupKey][id];
      });
      return { ...prev, [groupKey]: kept };
    });
  };

  const handleSelectExam = (groupKey, subjectId, examId) => {
    setExamBySubject((prev) => {
      const next = { ...(prev[groupKey] || {}) };
      if (examId) {
        next[subjectId] = examId;
      } else {
        delete next[subjectId];
      }
      return { ...prev, [groupKey]: next };
    });
  };

  const buildGroupPayload = (groupKey) =>
    (selectedSubjects[groupKey] || []).map((subjectId) => ({
      subject: subjectId,
      exam: examBySubject[groupKey]?.[subjectId],
    }));

  const resetSubjectSelection = () => {
    setSelectedSubjects({ mandatory: [], language: [], curricular: [] });
    setExamBySubject({ mandatory: {}, language: {}, curricular: {} });
  };

  // Selections belong to a class, so drop them whenever the class changes.
  useEffect(() => {
    resetSubjectSelection();
  }, [classId]);
  //fetch exam
  async function fetchExam(id) {
    const res = await axios({
      method: "get",
      url: `${serverUrl}/exam-v2/?class=${id}`,
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  }
  const exam = useQuery(["exam", getClassId], () => fetchExam(getClassId), {
    enabled: !!getClassId,
  });

  async function fetchTemplate(id) {
    const res = await axios({
      method: "get",
      url: `${serverUrl}/progress-card-template-v2/?class=${id}`,
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  }

  const getTemplate = useQuery(
    ["progress-card-template", getClassId],
    () => fetchTemplate(getClassId),
    {
      enabled: !!getClassId,
    },
  );
  const filteredData =
    getTemplate?.isSuccess &&
    getTemplate?.data?.result.length > 0 &&
    getTemplate?.data?.result?.filter(
      (item) =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        item?.class?._id === getClassId,
    );

  // The dates attendance may be counted over: the class's own academic start
  // and end (Settings > Class & Academic), each falling back to the school's.
  const academicRangeFor = (id) => {
    const record = (classes?.data ?? []).find?.((c) => c._id === id);
    const start =
      record?.academic_start_date || defaultDates?.data?.academic_start_date;
    const end =
      record?.academic_end_date || defaultDates?.data?.academic_end_date;
    const range = {
      start: start ? moment(start).startOf("day") : null,
      end: end ? moment(end).endOf("day") : null,
    };
    // A start after the end means the settings are wrong; do not lock out.
    if (range.start && range.end && range.start.isAfter(range.end)) {
      return { start: null, end: null };
    }
    return range;
  };
  const isOutsideRange = (date, range) =>
    !!date &&
    ((range.start && moment(date).isBefore(range.start)) ||
      (range.end && moment(date).isAfter(range.end)));
  const academicRange = academicRangeFor(classId);

  const onFinish = (values) => {
    const attendanceFrom = values?.attendance_from;
    const attendanceTo = values?.attendance_to;
    if (
      isOutsideRange(attendanceFrom, academicRange) ||
      isOutsideRange(attendanceTo, academicRange)
    ) {
      message.error(
        academicRange.start && academicRange.end
          ? `Attendance dates must be within the academic year ${academicRange.start.format(
              "DD-MM-YYYY",
            )} to ${academicRange.end.format("DD-MM-YYYY")}`
          : "Attendance dates must be within the academic year",
      );
      return;
    }
    if (!!attendanceFrom !== !!attendanceTo) {
      message.error("Set both attendance dates, or leave both empty");
      return;
    }
    if (
      attendanceFrom &&
      attendanceTo &&
      moment(attendanceTo).isBefore(moment(attendanceFrom), "day")
    ) {
      message.error("Attendance end date is before the start");
      return;
    }

    const payload = {
      class: classId,
      year: academicYear,
      name: values?.title,
      ...(attendanceFrom && {
        attendance_from: moment(attendanceFrom).format("YYYY-MM-DD"),
        attendance_to: moment(attendanceTo).format("YYYY-MM-DD"),
      }),
      mandatory_subjects: buildGroupPayload("mandatory"),
      languages: buildGroupPayload("language"),
      co_curricular: buildGroupPayload("curricular"),
    };

    // A template with a single subject is not a useful progress card.
    if (
      payload.mandatory_subjects.length +
        payload.languages.length +
        payload.co_curricular.length <
      2
    ) {
      message.error("Select two or more subjects for this template");
      return;
    }

    axios
      .post(serverUrl + "/progress-card-template-v2/", payload, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      })
      .then((res) => {
        form.resetFields();
        resetSubjectSelection();
        setClassId("");
        setAddTemplate(false);
        queryClient.refetchQueries(["progress-card-template", getClassId]);
        message.success(res?.data?.result?.message);
      })
      .catch((err) => {
        if (err?.response?.data?.error?.errors) {
          message.error(err?.response?.data?.error?.errors);
        }
      });
  };
  const handleDelete = async (id) => {
    try {
      await axios.delete(`${serverUrl}/progress-card-template-v2/${id}`, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      });
      queryClient.refetchQueries(["progress-card-template", getClassId]);
      message.success("Template deleted successfully");
    } catch (error) {
      message.error(error?.response?.data?.error?.errors);
    }
  };
  return (
    <>
      <div className="template">
        <div className="pt-8">
          <div className="bg-white p-10 rounded-2xl">
            <div className="text-2xl text-orange-500 font-semibold pb-8">
              Template
            </div>
            <div className="flex justify-between pb-4">
              <div>
                <Select
                  showSearch
                  style={{ width: 200 }}
                  placeholder="Select a class"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  filterSort={(optionA, optionB) =>
                    optionA.children
                      .toLowerCase()
                      .localeCompare(optionB.children.toLowerCase())
                  }
                  onChange={handleGetClass}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  value={getClassId}
                >
                  {classes?.isSuccess &&
                    classes?.data?.map((c) => (
                      <Option key={c?._id} value={c?._id}>
                        {c?.name}
                      </Option>
                    ))}
                </Select>
              </div>
              <Search
                placeholder="input search text"
                enterButton="Search"
                size="large"
                loading={false}
                style={{ backgroundColor: "#E3E3E3" }}
                prefix={<BiSearch className="text-gray-600 " />}
                onSearch={(value) => {
                  setSearchQuery(value);
                  sessionStorage.setItem("instructor_v2_search_query", value);
                }}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  sessionStorage.setItem(
                    "instructor_v2_search_query",
                    e.target.value,
                  );
                }}
                className="w-310"
                value={searchQuery}
              />
            </div>
            <div className="flex justify-between items-start pt-4">
              <div className="flex gap-20px flex-wrap	">
                {filteredData && filteredData ? (
                  filteredData?.map((list) => (
                    <div>
                      <div
                        className="iskool-friends-card flex flex-col space-x-3 cursor-pointer h-full p-20px"
                        key={list?._id}
                        style={{ width: "190px" }}
                      >
                        <div
                          className="text-center mt-14px"
                          onClick={() => {
                            navigate(
                              `/dashboard/instructor/menu/report/template/${list?._id}`,
                            );
                          }}
                        >
                          <UnorderedListOutlined size={35} />
                          <div className="pt-2">
                            <div className="font-bold capitalize">
                              {list?.class?.name}
                            </div>
                            <div className="font-bold capitalize line-clamp-1">
                              {list?.name}
                            </div>
                          </div>
                        </div>
                        {/* Once cards exist for a template, deleting it
                            would orphan them, so the action is withdrawn
                            rather than offered and refused. */}
                        <div className="flex justify-center pt-2">
                          {list?.card_count > 0 ? (
                            <span className="text-xs text-gray-500">
                              {list.card_count} report card
                              {list.card_count === 1 ? "" : "s"} generated
                            </span>
                          ) : (
                            <Popconfirm
                              title="Are you sure to delete?"
                              onConfirm={() => handleDelete(list?._id)}
                              okText="Yes"
                              cancelText="No"
                            >
                              <Button
                                type="danger flex items-center justify-center"
                                shape="circle"
                                icon={
                                  <DeleteOutlined
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  />
                                }
                              />
                            </Popconfirm>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div>No Template Exist</div>
                )}
              </div>
              <div
                onClick={() => setAddTemplate(true)}
                className="cursor-pointer p-28px add-module flex items-center justify-center mt-2"
              >
                <div className="flex flex-col items-center space-y-3">
                  <div className="flex items-center justify-center cursor-pointer">
                    <img className="w-6 h-6" src={plusicon} alt="icon" />
                  </div>
                  <div className="text-white text-sm ">
                    <div>Add New</div>
                    <div>Template</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ----------------------------------modal------------------------------------ */}
        {/* Add Template */}
        <Modal
          width={800}
          visible={addTemplate}
          onOk={() => setAddTemplate(false)}
          onCancel={() => {
            setClassId(null);
            form.resetFields();
            setAddTemplate(false);
            resetSubjectSelection();
            removeSubjectsData();
          }}
          footer={null}
          // Ticking a full subject list makes this form taller than the
          // viewport; without a scrolling body the Save row at the bottom
          // ends up off screen.
          bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
          // initialValues={initvalues}
          forceRender
        >
          <div>
            <h3 className="text-lg font-bold pb-3">Add Template</h3>
            <div className="pt-4 w-4/5">
              <Form
                form={form}
                name="basic"
                onFinish={onFinish}
                autoComplete="off"
                layout="vertical"
              >
                <Form.Item
                  label="Template Name"
                  name="title"
                  className="w-30"
                  rules={[
                    {
                      required: true,
                      message: "Template Name is required",
                    },
                  ]}
                >
                  <Input placeholder="Enter name" />
                </Form.Item>
                <Form.Item label="Academic Year" className="w-30">
                  <Input
                    value={academicYear}
                    disabled
                    className="w-full"
                    title="Set in Settings > Class & Academic"
                  />
                </Form.Item>
                <Form.Item
                  label="Class"
                  name="class"
                  className="w-full"
                  rules={[
                    {
                      required: true,
                      message: "Please select a class",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    showArrow
                    placeholder="Select Class"
                    className="w-full bg-gray-300"
                    style={{
                      border: "6px",
                      backgroundColor: "#EBEBEB",
                    }}
                    getPopupContainer={(trigger) => trigger.parentNode}
                    onChange={(e) => {
                      setClassId(e);
                      // A class with different dates can leave an earlier pick
                      // outside its academic year.
                      const next = academicRangeFor(e);
                      ["attendance_from", "attendance_to"].forEach((key) => {
                        if (isOutsideRange(form.getFieldValue(key), next)) {
                          form.setFieldsValue({ [key]: undefined });
                        }
                      });
                    }}
                    // onChange={(e) => {
                    //   setClassId(e)
                    //   form.setFieldsValue("language":null);
                    // }}
                  >
                    {classes?.isSuccess &&
                      classes?.data?.map((data) => {
                        return (
                          <Option key={data._id} value={data._id}>
                            {data.name}
                          </Option>
                        );
                      })}
                  </Select>
                </Form.Item>
                {/* Optional. The card's days present / absent are counted over
                    this period; leave both empty to show none. */}
                <div className="flex gap-4">
                  <Form.Item
                    label="Attendance From"
                    name="attendance_from"
                    className="w-full"
                  >
                    <DatePicker
                      className="w-full"
                      format="DD-MM-YYYY"
                      disabledDate={(d) => isOutsideRange(d, academicRange)}
                      defaultPickerValue={academicRange.start || undefined}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Attendance To"
                    name="attendance_to"
                    className="w-full"
                  >
                    <DatePicker
                      className="w-full"
                      format="DD-MM-YYYY"
                      disabledDate={(d) => isOutsideRange(d, academicRange)}
                      defaultPickerValue={academicRange.start || undefined}
                    />
                  </Form.Item>
                </div>
                {/* Step 1 - tick the subjects this template covers. Groups with
                    no subjects for the class are omitted entirely. */}
                {classId && (
                  <div className="w-full pb-4">
                    <div className="pb-2 font-semibold">
                      Select Subjects{" "}
                      <span className="font-normal text-gray-500 text-xs">
                        (select two or more)
                      </span>
                    </div>
                    {SUBJECT_GROUPS.filter(
                      (group) => groupSubjects(group.flag).length > 0,
                    ).map((group) => (
                      <div key={group.key} className="pb-3">
                        <div className="text-sm text-gray-600 pb-1">
                          {group.label}
                        </div>
                        <Checkbox.Group
                          className="w-full"
                          value={selectedSubjects[group.key]}
                          onChange={(checkedIds) =>
                            handleToggleSubjects(group.key, checkedIds)
                          }
                        >
                          {/* Grid rather than wrap, so names line up in
                              columns instead of going ragged. The inline
                              margin reset cancels antd's
                              `.ant-checkbox-wrapper + .ant-checkbox-wrapper`
                              rule, which otherwise stacks on the grid gap and
                              staggers every item after the first. */}
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2">
                            {groupSubjects(group.flag).map((data) => (
                              <Checkbox
                                key={data._id}
                                value={data._id}
                                className="capitalize"
                                style={{
                                  marginInlineStart: 0,
                                  alignItems: "center",
                                }}
                              >
                                {data?.name}
                              </Checkbox>
                            ))}
                          </div>
                        </Checkbox.Group>
                      </div>
                    ))}
                    {SUBJECT_GROUPS.every(
                      (group) => groupSubjects(group.flag).length === 0,
                    ) && (
                      <div className="text-sm text-black text-center">
                        No subjects found for this class
                      </div>
                    )}
                  </div>
                )}

                {/* Step 2 - assign an exam to each ticked subject. A group with
                    nothing ticked renders no label and no table. */}
                {SUBJECT_GROUPS.filter(
                  (group) => (selectedSubjects[group.key] || []).length > 0,
                ).map((group) => (
                  <Form.Item
                    key={group.key}
                    label={group.label}
                    className="w-full"
                  >
                    <div className="flex flex-col gap-3 border">
                      {groupSubjects(group.flag)
                        .filter((data) =>
                          selectedSubjects[group.key].includes(data._id),
                        )
                        .map((data) => (
                          <div
                            key={data._id}
                            className="bg-gray-200 px-4 py-2 border-b border-gray-500 last-border text-gray-700 capitalize flex items-center flex-row justify-between"
                          >
                            {data?.name}
                            <Select
                              showArrow
                              tagRender={tagRender}
                              placeholder="Select exam"
                              style={{
                                border: "6px",
                                backgroundColor: "#EBEBEB",
                                width: "50%",
                              }}
                              value={
                                examBySubject[group.key]?.[data._id] ??
                                undefined
                              }
                              optionFilterProp="children"
                              filterOption={(input, option) =>
                                option.children
                                  .toLowerCase()
                                  .indexOf(input.toLowerCase()) >= 0
                              }
                              getPopupContainer={(trigger) =>
                                trigger.parentNode
                              }
                              onChange={(examId) =>
                                handleSelectExam(group.key, data._id, examId)
                              }
                              onClear={() =>
                                handleSelectExam(group.key, data._id, null)
                              }
                              allowClear
                            >
                              {subjectExams(group.flag, data._id).map(
                                (examOption) => (
                                  <Option
                                    key={examOption?._id}
                                    value={examOption?._id}
                                  >
                                    {examOption?.name}
                                  </Option>
                                ),
                              )}
                            </Select>
                          </div>
                        ))}
                    </div>
                  </Form.Item>
                ))}

                <div className="flex justify-center items-center py-5 sticky bottom-0 bg-white z-10 border-t border-gray-200">
                  <div className="inline-flex items-center space-x-2">
                    <Button
                      size="large"
                      type="primary"
                      htmlType="submit"
                      className="text-white"
                    >
                      Save
                    </Button>
                    <Button
                      size="large"
                      type="dark"
                      onClick={() => {
                        form.resetFields();
                        setClassId(null);
                        setAddTemplate(false);
                        removeSubjectsData();
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </Form>
            </div>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default TemplateCard;
